import {Dispatch, FormEvent, memo} from 'react';

import styled, {useTheme} from 'styled-components';

import {FlexColumn, flexColumn} from '~/shared/theme/FlexLayout';
import {bodyBold, headingXS, smallRegular} from '~/shared/theme/typography';
import {getLocalizationService} from '~/shared/services/localisationService';
import CreditCardsDropdown from '~/shared/components/Otl/components/CreditCardsDropdown';
import PieButton from '~/shared/theme/pie/PieButton';
import OtlTermsOfUseCheckbox from '~/shared/components/Otl/OtlTermsOfUseCheckbox';
import {media} from '~/shared/theme/media';
import StatusBar from '~/shared/components/StatusBar';
import checkCircleFilledIconUrl from '~/assets/images/checkCircleFilled.svg';

const Form = styled.form`
  ${flexColumn}
  padding: 32px;
  flex: 1;
  justify-content: space-between;
  ${media.minLargeMobile`
    overflow: hidden;
    height: 336px;
    flex: auto;
    gap: 40px;
  `}
`;

const CardsSection = styled(FlexColumn)`
  gap: 20px;
`;

const ConfirmSection = styled(FlexColumn)`
  gap: 24px;
`;

const TitleContainer = styled.div`
  ${flexColumn}
  gap: 4px;
`;

const Title = styled.h3`
  ${bodyBold}
`;

const Description = styled.span`
  ${smallRegular}
  display: block;
`;

const Header = styled.p`
  ${headingXS}
  color: ${({theme}) => theme.colors.charcoal80};
  padding: 18px 0 17px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  text-align: center;
`;

type LinkMoneycardFormProps = {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  currentCardId: number | null;
  setCurrentCardId: Dispatch<number | null>;
  userCreditcards: {
    creditcardId: number;
    creditCardType: string;
    cardLastDigits: string;
  }[] | undefined;
  addCreditCardAction: () => void;
  hasSuccessfullyAddedCreditCard?: boolean;
  isDropdownTouched: boolean;
  setIsDropdownTouched: Dispatch<boolean>;
  isTermsOfUseConfirmed: boolean;
  toggleCheckbox: () => void;
};

const LinkMoneycardForm = ({
  onSubmit,
  currentCardId,
  setCurrentCardId,
  userCreditcards,
  addCreditCardAction,
  hasSuccessfullyAddedCreditCard,
  isDropdownTouched,
  setIsDropdownTouched,
  isTermsOfUseConfirmed,
  toggleCheckbox,
}: LinkMoneycardFormProps) => {
  const {t} = getLocalizationService();
  const theme = useTheme();

  return (
    <>
      <Header id="modal-title">
        {t('link_credit_card_to_10bis')}
      </Header>
      <Form onSubmit={onSubmit}>
        <CardsSection>
          <TitleContainer>
            <Title>{t('credit_card_to_charge')}</Title>
            <Description>{t('this_is_the_card_we_will_charge_when_your_10bis_budget_runs_out')}</Description>
          </TitleContainer>
          <CreditCardsDropdown
            selectedCreditCard={currentCardId}
            setSelectedCreditCard={setCurrentCardId}
            creditCardList={userCreditcards}
            addCreditCard={addCreditCardAction}
            onClick={() => setIsDropdownTouched(true)}
          />
          {hasSuccessfullyAddedCreditCard && !isDropdownTouched && (
            <StatusBar
              text={t('credit_card_was_successfully_added')}
              bgColor={theme.colors.feedbackSupportPositiveTonal}
              iconUrl={checkCircleFilledIconUrl}
            />
          )}
        </CardsSection>
        <ConfirmSection>
          <div>
            <OtlTermsOfUseCheckbox onChange={toggleCheckbox} />
          </div>
          <PieButton
            type="submit"
            variant="primaryXL"
            disabled={!isTermsOfUseConfirmed}
          >
            {t('confirm_and_link_card')}
          </PieButton>
        </ConfirmSection>
      </Form>
    </>
  );
};

export default memo(LinkMoneycardForm);
